import {
	main, section, aside, div
} from 'iblokz-snabbdom-helpers';

import layoutDoc from '../../../../config/layout.yml';
import {resolve} from '../util/layout';
import renderWidget from './widgets';

import content from './content';
import rightPane from './right-pane';

const BASE_LAYOUT_ID = 'base-layout';

/** Public static pages that fill `content` from YAML + i18n widgets. */
const STATIC_LAYOUTS = {
	home: 'home-page-layout',
	about: 'about-page-layout'
};

const tagFor = (node) => {
	const name = (node.tag || 'div').toLowerCase();
	if (name === 'main') {
		return main;
	}
	if (name === 'aside') {
		return aside;
	}
	if (name === 'section') {
		return section;
	}
	return div;
};

const classFor = (node) => {
	const parts = [];
	if (node.id) {
		parts.push(node.id);
	}
	if (node.kind && node.type === 'panel') {
		parts.push(`panel-${node.kind}`);
	}
	return parts.length ? `.${parts.join('.')}` : '';
};

/**
 * Render a resolved panel tree. `slots[id]` replaces that node entirely
 * (return null/false/[] to omit). Widgets use the kind registry.
 */
const renderNode = (node, ctx) => {
	const {slots = {}} = ctx;
	if (!node) {
		return [];
	}

	if (node.id && Object.prototype.hasOwnProperty.call(slots, node.id)) {
		const slotted = slots[node.id]({...ctx, node});
		if (slotted == null || slotted === false) {
			return [];
		}
		return slotted;
	}

	if (node.type === 'widget') {
		return renderWidget(node, ctx);
	}

	const tag = tagFor(node);
	const sel = classFor(node);
	const children = (node.children || []).map((child) =>
		renderNode(child, ctx)
	);

	const childrenFlat = [].concat(...children.map((c) => [].concat(c)));
	return sel ? tag(sel, childrenFlat) : tag(childrenFlat);
};

const isProfileDetail = (path = []) =>
	path[0] === 'profiles' && Boolean(path[1]);

const isHome = (path = [], page) =>
	page === 'home' || !path[0] || path[0] === 'home';

const layoutIdFor = (path = []) => {
	const page = path[0] || 'home';
	if (isProfileDetail(path)) {
		return 'profile-detail-layout';
	}
	return STATIC_LAYOUTS[page] || BASE_LAYOUT_ID;
};

export default ({state, actions, i18n}) => {
	const entries = (layoutDoc && layoutDoc.layout) || [];
	const path = (state.router && state.router.path) || [];
	const page = (state.router && state.router.page) || (path[0] || 'home');
	const layoutId = layoutIdFor(path);
	const tree = resolve(entries, layoutId);
	const isAdmin = path[0] === 'admin';
	const hideRightPane = isAdmin || isProfileDetail(path) || isHome(path, page);
	const isStatic = Boolean(STATIC_LAYOUTS[path[0] || 'home']);

	const ctx = {state, actions, i18n};
	const slots = {
		// Static pages: content comes from YAML widgets. Legacy pages: page switch.
		...(isStatic
			? {}
			: {content: () => content({state, actions, i18n})}),
		'right-pane': hideRightPane
			? () => null
			: () => rightPane({state, actions, i18n})
	};

	if (!tree) {
		return main([].concat(
			content({state, actions, i18n}),
			hideRightPane ? [] : rightPane({state, actions, i18n})
		));
	}

	return renderNode(tree, {...ctx, slots});
};
